import { useAiRecommendationCta } from '@cobliteam/ai';
import useAiRecommendationsStore, { type AiRecommendationJourney, permissionsConfig } from '@cobliteam/hooks/src/useAiRecommendationsStore/useAiRecommendationsStore';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { useTranslation } from '@cobliteam/i18n';
import usePermissions from '@cobliteam/permissions/src/hooks/usePermissions.tsx';
import Button from '@cobliteam/ui/components/Button';
import Tag from '@cobliteam/ui/components/Tag';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import AiImage from './assets/ai-image.svg?react';
import AiRecommendationBannerSkeleton from './components/AiRecommendationBannerSkeleton.tsx';

interface AiRecommendationBannerProps {
  journeyId: AiRecommendationJourney;
  primaryLabel?: string;
  onPrimaryAction?: () => void;
  secondaryLabel?: string;
  onSecondaryAction?: () => void;
}

const AiRecommendationBanner = ({ journeyId, primaryLabel, onPrimaryAction, secondaryLabel, onSecondaryAction }: AiRecommendationBannerProps) => {
  const { t } = useTranslation('app-header');
  const { aiRecommendations2 } = useFeatureFlags();
  const { recommendations, progress, isLoading, isError, dismissRecommendation } = useAiRecommendationsStore();
  const { isAccessAllowed } = usePermissions();

  const { onCta, ctaLabel } = useAiRecommendationCta({
    component: 'ai-recommendation-banner',
    journeyId: journeyId,
    primaryLabel: primaryLabel,
    onPrimaryAction: onPrimaryAction,
  });

  if (!aiRecommendations2) return null;

  const recommendation = recommendations.find((r) => r.journeyId === journeyId) ?? null;

  if (isLoading) return <AiRecommendationBannerSkeleton />;
  if (!recommendation || isError) return null;

  const permission = permissionsConfig[journeyId];

  if (!isAccessAllowed(permission)) {
    return null;
  }

  const handleDismiss = () => {
    if (onPrimaryAction) {
      onPrimaryAction();
    } else {
      dismissRecommendation(recommendation.journeyId, recommendation.recommendationType);
    }
  };

  const allCompleted = progress.totalJourneys > 0 && progress.completedJourneys >= progress.totalJourneys;

  return (
    <div data-testid='ai-recommendation-banner' className='flex w-full justify-between rounded-2xl border border-[#d9d9e0] border-solid bg-[#fcfdfe] p-[14px]'>
      <div className='gap-4'>
        <div className='flex gap-2'>
          <div className='flex'>
            <AiImage aria-hidden className='size-[19px] text-[#A1A1AA]' />
          </div>

          {/* Content */}
          <div className='flex min-w-0 flex-col gap-2'>
            <div className='flex items-center gap-3'>
              <Typography variant='description-title' className='text-[#0a0a0a]'>
                {recommendation.journeyName}
              </Typography>
              <Tag variant='surface' color='attention' size='sm' label={t('aiRecommendations.recommendationTag')} icon={<Icon name='triangle-alert' size='xs' />} />
            </div>
            <Typography variant='description-list-default' color='secondary' className='text-start'>
              {recommendation.shortDescription}
            </Typography>
          </div>
        </div>
      </div>

      {/* Buttons */}
      {!allCompleted && (
        <div className='flex items-center justify-end gap-2 pt-1'>
          <Button variant='ghost' color='primary' size='sm' onClick={handleDismiss}>
            {t('aiRecommendations.dismiss')}
          </Button>
          <Button variant='contained' color='primary' size='sm' onClick={onCta}>
            {ctaLabel}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AiRecommendationBanner;
